'use client'

import React from 'react'
import { useSearchParams } from 'next/navigation'

type AlertVariant = 'info' | 'error' | 'warning' | 'success'

const variantStyles: Record<AlertVariant, string> = {
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  success: 'bg-green-50 border-green-200 text-green-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  error: 'bg-red-50 border-red-200 text-destructive',
}

interface AuthAlertProps {
  /** Additional params to check beyond the defaults */
  extraParams?: string[]
  /** Optionally override message for a specific variant */
  overrides?: Partial<Record<AlertVariant, string>>
}

/**
 * AuthAlert — reads URL search params (info, error, warning, success) and
 * renders styled alert banners inside auth forms.
 *
 * Must be used inside a Suspense boundary (handled by AuthCard or the page).
 */
export function AuthAlert({ overrides }: AuthAlertProps) {
  const searchParams = useSearchParams()

  const params: { key: AlertVariant; label?: string }[] = [
    { key: 'info' },
    { key: 'success' },
    { key: 'warning' },
    { key: 'error' },
  ]

  const alerts = params
    .map(({ key }) => ({
      key,
      message: overrides?.[key] ?? searchParams.get(key),
    }))
    .filter((a) => Boolean(a.message))

  if (!alerts.length) return null

  return (
    <div className="space-y-2">
      {alerts.map(({ key, message }) => (
        <div
          key={key}
          role={key === 'error' || key === 'warning' ? 'alert' : 'status'}
          aria-live={key === 'error' || key === 'warning' ? 'assertive' : 'polite'}
          className={`rounded-2xl border px-4 py-3 text-sm ${variantStyles[key]}`}
        >
          {message}
        </div>
      ))}
    </div>
  )
}
